import { EMPTY_GRID_CLASS } from './Constants';
import { error } from './Util';

export default class QuerySelector {
  private constants;
  private readonly origin;
  private readonly originPath;
  private trees;
  public pathSelector;

  constructor(origin) {
    this.constants = {
      nthType: ':nth-of-type',
      nthChild: ':nth-child'
    };
    this.origin = origin;
    this.originPath = this.walkUp(this.origin);
    this.trees = this.findTrees();
    this.pathSelector = this.diffTreePaths();
  }

  private querySelectorValid = (name = 'querySelector', selector: string, el = document) => {
    let domEl;
    try {
      domEl = el[name](selector);
    } catch (e) {
      domEl = null;
      if (name === 'querySelectorAll') {
        domEl = [];
      }
      // <debug>
      error(`You have querySelector exception! See QuerySelector.ts class! -> \n${e}`, 'warn');
      // </debug>
    }
    return domEl;
  };

  /**
   * Diffs tree paths of this.trees to find which selectors we need to add to this.originPath
   */
  private diffTreePaths() {
    return this.trees.map(tree => this.stringify(tree));
    // console.log(JSON.stringify(a, null, 2));
  }

  /**
   * Finds element parent trees
   */
  private findTrees() {
    let maxPath = this.stringify(this.originPath);
    let minPath = this.maxLeftTrim(this.leftTrimId(maxPath));
    const objList = this.querySelectorValid('querySelectorAll', minPath.join(' '));
    return [].slice.call(objList).map(node => this.walkUp(node));
  }

  /**
   * Takes original target node and walks up tree and collects path
   * @param {HTMLElement} origin - needle in the haystack (dom tree) we start at
   * @param {HTMLElement} el
   * @param {Object[]} path
   */
  private walkUp(origin, el?, path = []) {
    let target = el || origin;
    let position = this.walkLeft(target);
    let node: any = {
      name: target.nodeName.toLowerCase(),
      nthType: position.type,
      nthChild: position.child
    };
    if (target.attributes && target.attributes.length > 0) {
      node.attrs = {};
      [].slice.call(target.attributes).forEach((attr) => {
        node.attrs[attr.name] = attr.value;
      });
    }
    if (target.parentNode) {
      path.unshift(node);
      return this.walkUp(origin, target.parentNode, path);
    } else {
      return path;
    }
  }

  /**
   * Takes node and walks left in list of sibling nodes to get nth-type-of and nth-child positions, which starts at 1, not 0.
   * @param {dom object} node
   */
  private walkLeft(node) {
    return !node.parentNode ? 0 : [].slice.call(node.parentNode.childNodes || []).reduce((acc, el) => {
      if (el === node) {
        acc.hasPosition = true;
      }
      if (acc.hasPosition === false) {
        acc.position.child++;
        if (el.nodeName === node.nodeName) {
          acc.position.type++;
        }
      }
      return acc;
    }, { position: { type: 1, child: 1 }, hasPosition: false }).position;
  }

  /**
   * Takes query selector array path and left trims to the last #id selector
   * @param {array} path - array of selector strings
   */
  private leftTrimId(path) {
    return path.reverse().reduce((acc, item) => {
      if (acc.join(' ').indexOf('#') < 0) {
        acc.push(item);
      }
      return acc;
    }, []).reverse();
  }

  /**
   * Takes query selector path array and recursively left trims it until the original node is found
   * @param {String[]} path - not a collection, just array of strings
   * @param {String[]} origin Origin path array
   * @param {Number} trim
   * @param {String[]} previous
   */
  private maxLeftTrim(path, origin?, trim = 0, previous?) {
    if (!origin) {
      const originPath = path.join(' ');
      origin = this.querySelectorValid('querySelector', originPath);
    }
    let branch = path.slice(trim);
    let selector = branch.join(' ');
    let target;
    if (branch.length === 1) {
      return branch;
    }
    if (branch.length === 0) {
      return ['body'];
    }
    target = this.querySelectorValid('querySelector', selector);
    if (origin === target) {
      return this.maxLeftTrim(path, origin, trim + 1, branch);
    } else if (origin !== target || target === null) {
      return previous;
    }
  }

  /**
   * Takes collection of nodes and returns concatenated strings
   * @param {array} path
   * @property {string} name - node name
   * @property {object} attrs - html attributes, each key/value is a string
   */
  private stringify(path) {
    return path.map((node) => {
      let name = node.name;
      let nthType = node.nthType < 2 ? '' : `${this.constants.nthType}(${node.nthType})`;
      let nthChild = node.nthChild < 2 ? '' : `${this.constants.nthChild}(${node.nthChild})`;
      let id = '';
      let classList = '';
      let blank = /^\s+$/;
      let spaces = /\s{2,}/g;
      let selector = '';
      if (node.attrs) {
        if (node.attrs.id) {
          id = node.attrs.id.trim();
        }
        if (node.attrs.class) {
          classList = '.' + node.attrs.class.trim().replace(spaces, ' ').split(' ').join('.').trim();
        }
      }
      if ((blank.test(id) && blank.test(classList)) || (!id && !classList)) {
        selector = name + nthType;
      } else if (id) {
        if (name) {
          selector = `${name}[id="${id}"]${classList}`;
        } else {
          selector = '#' + id;
        }
      } else if (!id && classList) {
        selector = name + classList + nthType;
      }
      return selector;
    });
  }

  /**
   * Gets dom element based on selectors
   */
  public getDomElement = () => {
    let domElement = null;
    let selectors: any = this.pathSelector;
    if (selectors.length) {
      // <debug>
      if (selectors.length > 1) {
        error('There are many elements with same structure (Probably You have many elements with same id)!', 'warn');
      }
      // </debug>
      selectors = selectors[0];
      const selector = selectors.join(' ').replace(`.${EMPTY_GRID_CLASS}`, '');
      // removing EMPTY_GRID_CLASS because that class is conditionally added and it can result in not finding
      // element that is actually in dom
      domElement = this.querySelectorValid('querySelector', selector);
    }
    if (!domElement) {
      // <debug>
      error('Query selector can not find element!', 'warn');
      // </debug>
    }
    return domElement;
  }
}

